// Export Workflow — the scoped-review caveat (scope-selection plan Slice 5). A run the user narrowed
// to specific checks must never read as a complete export clearance. ONE place owns the
// wording + which checks ran / were not requested, so the live summary card (ExportResultsComp),
// Saved Work (ConsolidatedResults, 'Export Unified') and the PDF (EntityTicketPdf) agree. Reads
// `data.review_scope` (pyapi `_review_scope_for_wire`); self-hides on a full review and on runs
// saved before the scope existed. Pure TS so the PDF builder can use it.
import {
  EXPORT_REVIEW_PHASES,
  REVIEW_PHASE_LABELS,
  ExportAssessmentData,
  NOT_REQUESTED_LABEL,
  isPhaseNotRequested,
} from './exportAssessment';
import { ExportAssessmentPhase } from '../interfaces/iStreamedResponse';

export const SCOPED_REVIEW_TITLE = 'Scoped Review';
export const SCOPED_REVIEW_NOTICE =
  'Only the checks selected for this review were run. The checks marked "not requested" were ' +
  'not run and are not assessed — do not read this result as a complete export clearance.';
/** Per-section status for a phase the user scoped out (Saved Work / PDF sections). */
export const NOT_REQUESTED_SECTION_TEXT = `${NOT_REQUESTED_LABEL} — this check was not run, so it is not assessed.`;

export interface ScopedReviewSummaryData {
  inReview: ExportAssessmentPhase[];
  notRequested: ExportAssessmentPhase[];
  inReviewLabels: string[];
  notRequestedLabels: string[];
  pill: string;
}

const labelFor = (phase: ExportAssessmentPhase): string => REVIEW_PHASE_LABELS[phase];

/** What ran vs what was not requested; null on a full review / legacy run. */
export function scopedReviewSummary(data: ExportAssessmentData): ScopedReviewSummaryData | null {
  if (data?.review_scope?.scope !== 'specific') return null;
  const notRequested = EXPORT_REVIEW_PHASES.filter((p) => isPhaseNotRequested(data, p));
  if (!notRequested.length) return null;
  const inReview = EXPORT_REVIEW_PHASES.filter((p) => !notRequested.includes(p));
  return {
    inReview: inReview,
    notRequested: notRequested,
    inReviewLabels: inReview.map(labelFor),
    notRequestedLabels: notRequested.map(labelFor),
    pill: `Scoped review — ${inReview.length} of ${EXPORT_REVIEW_PHASES.length} checks run`,
  };
}

/** PDF / table rows for the Scoped Review section ([] on a full review → section self-hides). */
export function buildScopedReviewRows(data: ExportAssessmentData): Array<[string, string]> {
  const s = scopedReviewSummary(data);
  if (!s) return [];
  return [
    ['Review type', s.pill],
    ['In this review', s.inReviewLabels.join('\n')],
    ['Not requested (not run)', s.notRequestedLabels.join('\n')],
    ['What this means', SCOPED_REVIEW_NOTICE],
  ];
}
